/**
 * `/tree/:personId` — docs/08-frontend-and-ux.md §3.3, §4.7.
 *
 * Everything interesting about the view lives in the URL: the pivot in the path,
 * the view and depth in the query. That is not tidiness — it is what makes a
 * chart *shareable inside the family*, which is most of why anyone opens one.
 * "Look at where great-uncle Mehdi sits" is a link, and pivoting is a real
 * navigation, so the browser Back button walks back through the exploration.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { kinshipApi, treeApi } from '../../api/client.js';
import { useSession } from '../../app/session.js';
import { problemMessage } from '../../ui/problem.js';
import { TreeChart } from './TreeChart.js';
import { TREE_VIEWS, selectView, type TreeView } from './views.js';
import ui from '../../ui/components.module.css';
import styles from './tree.module.css';

/** How many pivots the breadcrumb trail keeps (§4.7). */
const TRAIL_LENGTH = 5;

export function TreePage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useSession();

  const view = (params.get('view') ?? 'ancestors') as TreeView;
  const generations = Number(params.get('gens') ?? '4');
  const showPending = params.get('pending') === 'true';
  const trail = params.getAll('trail');

  const canSeePending = user?.role === 'elder' || user?.role === 'admin';

  const scene = useQuery({
    queryKey: ['tree', id, generations, showPending],
    queryFn: () => treeApi.scene(id, generations, showPending && canSeePending),
    enabled: id !== '',
  });

  /*
   * "Who is this to me?" — the question a reader actually has while looking at a
   * stranger's card, answered by the M3 labeller rather than by anything the
   * chart itself knows. It is a separate query on purpose: the label depends on
   * the whole graph, not on the bounded scene being drawn.
   */
  const [inspecting, setInspecting] = useState<string | null>(null);
  const kinship = useQuery({
    queryKey: ['kinship-path', user?.person_id ?? '', inspecting ?? ''],
    queryFn: () => kinshipApi.path(user!.person_id!, inspecting!, 'shortest'),
    enabled: Boolean(user?.person_id) && Boolean(inspecting) && user?.person_id !== inspecting,
    staleTime: 5 * 60 * 1000,
  });

  const subset = useMemo(
    () => (scene.data ? selectView(scene.data, view, generations) : null),
    [scene.data, view, generations],
  );

  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next);
  };

  const pivot = (personId: string): void => {
    if (personId === id) return;
    const next = new URLSearchParams(params);
    // The trail is a breadcrumb of where the reader has been, capped so a long
    // exploration does not turn the URL into a transcript.
    next.delete('trail');
    for (const previous of [...trail, id].slice(-TRAIL_LENGTH)) next.append('trail', previous);
    navigate(`/tree/${personId}?${next.toString()}`);
  };

  if (id === '') {
    return (
      <div className={ui.card}>
        <h1>{t('tree.title')}</h1>
        <p className={ui.muted}>{t('tree.pickSomeone')}</p>
        <Link className={ui.buttonSecondary} to="/people">
          {t('people.title')}
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.toolbar}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>{t('tree.title')}</h1>

        <div role="group" aria-label={t('tree.view')}>
          {TREE_VIEWS.map((name) => (
            <button
              key={name}
              type="button"
              className={view === name ? ui.button : ui.buttonSecondary}
              aria-pressed={view === name}
              onClick={() => setParam('view', name)}
            >
              {t(`tree.views.${name}`)}
            </button>
          ))}
        </div>

        <label className={ui.field} style={{ margin: 0 }}>
          <span className={ui.label}>{t('tree.generations')}</span>
          <select
            className={ui.input}
            value={String(generations)}
            onChange={(event) => setParam('gens', event.target.value)}
          >
            {[2, 3, 4, 5, 6].map((depth) => (
              <option key={depth} value={depth}>
                {depth}
              </option>
            ))}
          </select>
        </label>

        {canSeePending && (
          <label>
            <input
              type="checkbox"
              checked={showPending}
              onChange={(event) => setParam('pending', String(event.target.checked))}
            />{' '}
            {t('tree.showPending')}
          </label>
        )}

        <span className={styles.spacer} />

        {trail.length > 0 && (
          <nav aria-label={t('tree.trail')}>
            {trail.slice(-TRAIL_LENGTH).map((previous, index) => (
              <Link
                key={`${previous}-${String(index)}`}
                to={`/tree/${previous}`}
                className={ui.buttonSecondary}
              >
                ←
              </Link>
            ))}
          </nav>
        )}

        <button type="button" className={ui.buttonSecondary} onClick={() => window.print()}>
          {t('tree.print')}
        </button>
      </div>

      {scene.isPending && <p className={ui.muted}>{t('app.loading')}</p>}
      {scene.isError && <p className={ui.problem}>{problemMessage(scene.error, t)}</p>}

      {subset && subset.persons.length > 0 && (
        <TreeChart
          scene={subset}
          hidden={scene.data?.hidden ?? {}}
          onPivot={pivot}
          onOpen={(personId) => navigate(`/people/${personId}`)}
          onInspect={setInspecting}
        />
      )}
      {subset && subset.persons.length <= 1 && (
        <div className={ui.card}>
          <p>{t('tree.nothingToDraw')}</p>
        </div>
      )}

      {/* A live region: the answer changes as the reader moves, and a screen
          reader should hear it without being interrupted mid-sentence. */}
      <p className={ui.muted} role="status" aria-live="polite">
        {kinship.data?.related && kinship.data.paths[0]
          ? kinship.data.paths[0].sentence
          : t('tree.hint')}
      </p>

      {/* §4.5 and §4.4 both encode meaning in stroke, so the legend is not
          decoration — it is the key to reading the chart at all. */}
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <svg width="40" height="8" aria-hidden="true">
            <line x1="0" y1="4" x2="40" y2="4" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          {t('tree.legend.biological')}
        </span>
        <span className={styles.legendItem}>
          <svg width="40" height="8" aria-hidden="true">
            <line
              x1="0"
              y1="4"
              x2="40"
              y2="4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeDasharray="6 3"
            />
          </svg>
          {t('tree.legend.adoptive')}
        </span>
        <span className={styles.legendItem}>
          <svg width="40" height="8" aria-hidden="true">
            <line
              x1="0"
              y1="4"
              x2="40"
              y2="4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeDasharray="2 3"
            />
          </svg>
          {t('tree.legend.foster')}
        </span>
        <span className={styles.legendItem}>
          <svg width="40" height="8" aria-hidden="true">
            <line
              x1="0"
              y1="4"
              x2="40"
              y2="4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeDasharray="1 3"
            />
          </svg>
          {t('tree.legend.guardianship')}
        </span>
        <span className={styles.legendItem}>
          <svg width="40" height="8" aria-hidden="true">
            <line
              x1="0"
              y1="4"
              x2="40"
              y2="4"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="10 4"
            />
          </svg>
          {t('tree.legend.endedUnion')}
        </span>
        <span className={styles.legendItem}>{t('tree.legend.stepNote')}</span>
      </div>
    </div>
  );
}
